/**
 * Minecraft skin geometry for Skin Studio: the modern 64×64 layout, Classic (4-pixel arms) and
 * Slim (3-pixel arms). Pure functions over RGBA pixel buffers so the browser and the server use
 * the same table. "Right"/"left" always mean the CHARACTER's right/left (the right arm is on
 * the viewer's left when the character faces you).
 */

export const SKIN_SIZE = 64;
export type Model = 'classic' | 'slim';
export type Part = 'head' | 'body' | 'rightArm' | 'leftArm' | 'rightLeg' | 'leftLeg';
export type Face = 'front' | 'back' | 'right' | 'left' | 'top' | 'bottom';
export type Layer = 'base' | 'outer';
export const PARTS: Part[] = ['head', 'body', 'rightArm', 'leftArm', 'rightLeg', 'leftLeg'];
export const FACES: Face[] = ['front', 'back', 'right', 'left', 'top', 'bottom'];
export const LAYERS: Layer[] = ['base', 'outer'];

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Texture origin of each part's box, per layer. */
const ORIGIN: Record<Part, Record<Layer, [number, number]>> = {
  head: { base: [0, 0], outer: [32, 0] },
  body: { base: [16, 16], outer: [16, 32] },
  rightArm: { base: [40, 16], outer: [40, 32] },
  leftArm: { base: [32, 48], outer: [48, 48] },
  rightLeg: { base: [0, 16], outer: [0, 32] },
  leftLeg: { base: [16, 48], outer: [0, 48] },
};

/** Box size (width, height, depth) in pixels. */
export function boxSize(part: Part, model: Model): [number, number, number] {
  if (part === 'head') return [8, 8, 8];
  if (part === 'body') return [8, 12, 4];
  if (part === 'rightArm' || part === 'leftArm') return [model === 'slim' ? 3 : 4, 12, 4];
  return [4, 12, 4];
}

/**
 * Where one face of one part lives in the texture. Standard box unwrap from the origin (u, v):
 *   top (u+d, v)   bottom (u+d+w, v)
 *   right (u, v+d)   front (u+d, v+d)   left (u+d+w, v+d)   back (u+2d+w, v+d)
 */
export function faceRect(part: Part, face: Face, layer: Layer, model: Model): Rect {
  const [u, v] = ORIGIN[part][layer];
  const [w, h, d] = boxSize(part, model);
  switch (face) {
    case 'top':
      return { x: u + d, y: v, w, h: d };
    case 'bottom':
      return { x: u + d + w, y: v, w, h: d };
    case 'right':
      return { x: u, y: v + d, w: d, h };
    case 'front':
      return { x: u + d, y: v + d, w, h };
    case 'left':
      return { x: u + d + w, y: v + d, w: d, h };
    case 'back':
      return { x: u + 2 * d + w, y: v + d, w, h };
  }
}

export type Pixels = Uint8ClampedArray; // 64*64*4 RGBA

export const blankPixels = (): Pixels => new Uint8ClampedArray(SKIN_SIZE * SKIN_SIZE * 4);
const idx = (x: number, y: number) => (y * SKIN_SIZE + x) * 4;

export function getPixel(p: Pixels, x: number, y: number): [number, number, number, number] {
  const i = idx(x, y);
  return [p[i]!, p[i + 1]!, p[i + 2]!, p[i + 3]!];
}
export function setPixel(p: Pixels, x: number, y: number, rgba: readonly number[]) {
  const i = idx(x, y);
  p[i] = rgba[0]!;
  p[i + 1] = rgba[1]!;
  p[i + 2] = rgba[2]!;
  p[i + 3] = rgba[3]!;
}

/** Every mapped rect for a model (both layers). */
export function allRects(model: Model, layer?: Layer) {
  return PARTS.flatMap((part) =>
    (layer ? [layer] : LAYERS).flatMap((l) =>
      FACES.map((face) => ({ part, face, layer: l, rect: faceRect(part, face, l, model) })),
    ),
  );
}

/** Base-layer pixels that are not fully opaque would show as holes in Minecraft. */
export function baseHoles(p: Pixels, model: Model): number {
  let n = 0;
  for (const { rect } of allRects(model, 'base'))
    for (let y = rect.y; y < rect.y + rect.h; y++)
      for (let x = rect.x; x < rect.x + rect.w; x++) if (p[idx(x, y) + 3] !== 255) n++;
  return n;
}

/**
 * Makes a skin safe to export: fills base-layer holes with an opaque colour (keeping the pixel's
 * own colour when it had one) and clears pixels outside the mapped areas.
 */
export function normalize(p: Pixels, model: Model, fill: readonly number[] = [0, 0, 0]): Pixels {
  const out = blankPixels();
  for (const { rect, layer } of allRects(model))
    for (let y = rect.y; y < rect.y + rect.h; y++)
      for (let x = rect.x; x < rect.x + rect.w; x++) {
        const [r, g, b, a] = getPixel(p, x, y);
        if (layer === 'outer') setPixel(out, x, y, [r, g, b, a]);
        else setPixel(out, x, y, a === 0 ? [fill[0]!, fill[1]!, fill[2]!, 255] : [r, g, b, 255]);
      }
  return out;
}

/**
 * Mirror painting: the pixel on the other side of the character that matches (x, y) on a face.
 * Limbs mirror to the other limb; the head and body mirror within themselves. Front, back, top
 * and bottom flip horizontally; the right side swaps with the left side (also flipped).
 */
export function mirrorOf(
  part: Part,
  face: Face,
  x: number,
  y: number,
  model: Model,
): { part: Part; face: Face; x: number; y: number } {
  const other: Record<Part, Part> = {
    head: 'head',
    body: 'body',
    rightArm: 'leftArm',
    leftArm: 'rightArm',
    rightLeg: 'leftLeg',
    leftLeg: 'rightLeg',
  };
  const mface: Face = face === 'right' ? 'left' : face === 'left' ? 'right' : face;
  const r = faceRect(other[part], mface, 'base', model);
  return { part: other[part], face: mface, x: r.w - 1 - x, y };
}

/**
 * Classic ↔ Slim. Not lossless: arm faces that are 4 wide become 3 wide (one column dropped)
 * or the reverse (a column duplicated). Callers keep the original and convert a copy.
 */
export function convertModel(p: Pixels, from: Model, to: Model): Pixels {
  if (from === to) return p.slice();
  const out = p.slice();
  for (const part of ['rightArm', 'leftArm'] as Part[])
    for (const layer of LAYERS) {
      // Clear the old arm area first, then resample each face into its new rect.
      for (const face of FACES) {
        const r = faceRect(part, face, layer, from);
        for (let y = r.y; y < r.y + r.h; y++)
          for (let x = r.x; x < r.x + r.w; x++) setPixel(out, x, y, [0, 0, 0, 0]);
      }
      for (const face of FACES) {
        const a = faceRect(part, face, layer, from);
        const b = faceRect(part, face, layer, to);
        for (let y = 0; y < b.h; y++)
          for (let x = 0; x < b.w; x++) {
            // Sample at pixel centres, so Slim → Classic → Slim gives back the original arms.
            const sx = Math.min(a.w - 1, Math.floor(((x + 0.5) * a.w) / b.w));
            setPixel(out, b.x + x, b.y + y, getPixel(p, a.x + sx, a.y + y));
          }
      }
    }
  return out;
}

/** Fill within one face only (4-connected, exact colour match). */
export function floodFill(p: Pixels, rect: Rect, sx: number, sy: number, rgba: readonly number[]) {
  const target = getPixel(p, rect.x + sx, rect.y + sy).join();
  if (target === rgba.join()) return;
  const stack: [number, number][] = [[sx, sy]];
  while (stack.length) {
    const [x, y] = stack.pop()!;
    if (x < 0 || y < 0 || x >= rect.w || y >= rect.h) continue;
    if (getPixel(p, rect.x + x, rect.y + y).join() !== target) continue;
    setPixel(p, rect.x + x, rect.y + y, rgba);
    stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
  }
}

// ---------- starter templates (original designs) ----------

type Paint = Partial<Record<Part, string>>;
function template(model: Model, base: Paint, extra?: (p: Pixels) => void): Pixels {
  const p = blankPixels();
  const rgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).concat(255);
  for (const part of PARTS)
    for (const face of FACES) {
      const r = faceRect(part, face, 'base', model);
      const c = rgb(base[part] ?? '#c69c6d');
      for (let y = r.y; y < r.y + r.h; y++)
        for (let x = r.x; x < r.x + r.w; x++) setPixel(p, x, y, c);
    }
  // Simple face: eyes and hair, the same for every template.
  const f = faceRect('head', 'front', 'base', model);
  for (const x of [1, 2, 5, 6])
    setPixel(p, f.x + x, f.y + 4, x === 2 || x === 5 ? rgb('#3b2a1a') : rgb('#ffffff'));
  for (const face of FACES) {
    const r = faceRect('head', face, 'base', model);
    const rows = face === 'top' ? r.h : face === 'bottom' ? 0 : face === 'front' ? 2 : 3;
    for (let y = 0; y < rows; y++)
      for (let x = 0; x < r.w; x++) setPixel(p, r.x + x, r.y + y, rgb('#5a3a1e'));
  }
  extra?.(p);
  return p;
}

export const SKIN_TEMPLATES: { id: string; name: string; make: (m: Model) => Pixels }[] = [
  {
    id: 'explorer',
    name: 'Explorer',
    make: (m) =>
      template(m, {
        body: '#3f7a3a',
        rightArm: '#3f7a3a',
        leftArm: '#3f7a3a',
        rightLeg: '#6b4a2b',
        leftLeg: '#6b4a2b',
      }),
  },
  {
    id: 'stargazer',
    name: 'Stargazer',
    make: (m) =>
      template(m, {
        body: '#2b3a7a',
        rightArm: '#2b3a7a',
        leftArm: '#2b3a7a',
        rightLeg: '#1f2440',
        leftLeg: '#1f2440',
      }),
  },
  {
    id: 'blank',
    name: 'Blank canvas',
    make: (m) =>
      template(m, {
        body: '#bbbbbb',
        rightArm: '#c69c6d',
        leftArm: '#c69c6d',
        rightLeg: '#8a8a8a',
        leftLeg: '#8a8a8a',
      }),
  },
];

/** Optional, self-declared creative ideas (never scored). */
export const SKIN_PROMPTS = [
  'Use only three colours.',
  'Design an explorer ready for a cave trip.',
  'Add a pattern to one sleeve.',
  'Give your character a cape-coloured back.',
  'Make a robot version of yourself.',
  'Use the outer layer to add a hat.',
  'Design a character who lives underwater.',
  'Make matching boots.',
  'Add a symbol on the chest.',
  'Make it look like a sunset.',
  'Design a sidekick for King Arachnid.',
  'Use mirror painting for a perfectly even design.',
];

export const SKIN_LIMITS = { designs: 20, versions: 5, nameLength: 40 } as const;
